import { Component, Inject, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserClient, IdentityDto, UserDto, UserRoleVm, UserVm, ExternalIdentity } from 'src/app/api';
import { FieldMetadata, FieldWidthEnum, ViewMetadata } from '../../../cer/cer-data/cer-data-types';
import { CerGridComponent } from '../../../cer/cer-grid/cer-grid.component';
import { UiActionTypeEnum, UiCommand, UiCommandEvent } from '@ui/cer-ui-types';
import { CerFormNavigationService } from '@form/cer-form-navigation.service';
import { CerFormContextService } from '@form/cer-form-context.service';
import { CerFormService } from '@form/cer-form.service';
import { CerPromptService, PromptInput } from '@ui/cer-ui-prompt.service';

@Component({
  selector: 'user-identity',
  templateUrl: './user-identity.component.html',
  styleUrls: ['./user-identity.component.css'],
  providers: [CerFormNavigationService, CerFormContextService, CerFormService]
})
export class UserIdentityComponent {

  // Identity view
  public viewMetadata: ViewMetadata = {
    name: 'identities', text: 'Logins', textSingular: 'Login',
    primaryKey: 'id', titleFields: ['name'], allowEdit: true, allowCreate: true, allowDelete: false
  };
  // User fields
  public fieldMetadata: FieldMetadata[] =
    [{ name: 'id', text: 'Id', tooltipText: 'Unik nøgle for login', allowEdit: false, widthType: FieldWidthEnum.XL, format: 'Text', visible: false },
    { name: 'email', text: 'E-mail', tooltipText: 'Brugers e-mail', allowEdit: false, defaultValue: '', widthType: FieldWidthEnum.L },
    { name: 'emailConfirmed', text: 'Bekræftet', tooltipText: 'Er konto bekræftet?', allowEdit: true, format: 'CheckBox' },
    { name: 'userShortName', text: 'Brugertilknytning', tooltipText: 'Er konto tilknyttet en bruger?', allowEdit: false, widthType: FieldWidthEnum.L }
    ];

  public toolbarCommands: UiCommand[] = [];

  public vm: UserRoleVm;

  @ViewChild(CerGridComponent, { static: false }) grid: CerGridComponent;

  constructor(private dialogRef: MatDialogRef<UserVm>, @Inject(MAT_DIALOG_DATA) callerData: any,
    private userClient: UserClient, private ui: CerPromptService) {
    // this.formService.formPersistenceComponents.push(this.grid); (not supported)
    this.vm = callerData.vm;
    this.viewMetadata.data = this.addUserToIdentity(this.vm.identities, this.vm.users, this.vm.user);
    this.viewMetadata.offline = true;
  }

  public addUserToIdentity(identities: IdentityDto[], users: UserDto[], user: UserDto): IdentityDto[] {

    // Add linked user short name
    identities.forEach(iDto => {
      var userFound = users.filter(u => u.appUserId == iDto.id);
      var i: any = <any>iDto;
      i.userShortName = (userFound.length > 0) ? userFound[0].shortName : '';
    });
    // Sort by users current identity first

    var hasUserLogin = (user.appUserId != null && user.appUserId.length > 0);
    var hasUserEmail = (user.email != null && user.email.length > 0);

    identities = identities.sort((i1, i2) => {
      if (hasUserLogin) {
        if (i1.id == user.appUserId) {
          return -1;
        }
        if (i2.id == user.appUserId) {
          return 1;
        }
      }
      if (hasUserEmail) {
        if (i1.email == user.email) {
          return -1;
        }
        if (i2.email == user.email) {
          return 1;
        }
      }
      return ((<any>i1).userShortName > (<any>i2).userShortName ? 1 : (<any>i1).userShortName < (<any>i2).userShortName ? -1 : 0);
    });
    return identities;
  }

  public onCommand(event: UiCommandEvent) {
    if (event.source == 'actionBegin' && event.actionType == UiActionTypeEnum.Add) {
      this.addGraphServerUser();
      event.cancel = true;
    }
    if (event.source == 'actionComplete' && event.actionType == UiActionTypeEnum.Save) {
      console.log(event.args);
      var identity = event.rowData as IdentityDto;
      this.userClient.setIdentityConfirmed(identity.id, identity.emailConfirmed).subscribe();
    }
  }

  private addGraphServerUser() {
    this.userClient.getExternalIdentities().subscribe({
      next: (extIdentities) => {
        this.addExternalIdentitySelect(extIdentities);
      },
      error: (error) => {
        this.addExternalIdentitySelect([]);
        //this.ui.errorApi(error);
      }
    });
  }

  private addExternalIdentitySelect(extIdentities: ExternalIdentity[]) {
    const existingIds = this.vm.identities.map(i => i.id);
    const newIdentities = extIdentities.filter(i => i.id != null && !existingIds.includes(i.id)); // TODO: existingIs must be from external id!!!
    var inputs: PromptInput[] = [
      {
        label: "Konto",
        id: "identityId",
        placeholder: 'Vælg eksisterende konto',
        valueNumber: null,
        type: (newIdentities.length > 0) ? 'DropDownList' : 'Input',
        dataSource: newIdentities,
        fields: { text: 'principalName', value: 'id' },
        element: null
      }
    ];

    this.ui.promptInputs('', 'Vælg konto', inputs).then(
      result => {
        if (result.action == 'ok') {
          var input = result.inputs[0];
          const id = (newIdentities.length > 0) ? input.ej2.value : (input.element as HTMLInputElement).value;
          if (id?.length > 0) {
            const identity = newIdentities.find(f => f.id == id);
            if (identity != null) {
              this.addExternalIdentitySelected(id, identity);
            }
          }
        }
      }
    );
  }

  private addExternalIdentitySelected(id: string, identity: ExternalIdentity) {
    if (id?.length > 0) {
            this.userClient.createExternalIdentity(id, identity?.name, identity?.email ?? identity?.principalName , null, null /*this.userId*/).subscribe({
        next: (externalId) => {
          if (externalId?.length > 0) {
            this.grid.research();
          }
          else {
            this.ui.error("Ingen login oprettet");
          }
        },
        error: (error) => {
          this.ui.errorApi(error)
        }
      })
    }
  }

  public saveAndclose() {
    var selected = this.grid.rowsSelectedData();

    if (selected.length != 1) {
      this.ui.error("Der skal markeres netop ét login");
      return;
    }

    const identity = selected[0] as IdentityDto;
    var linkedUserShortName: string = (selected[0] as any).userShortName;
    if (linkedUserShortName && linkedUserShortName.length > 0 && linkedUserShortName != this.vm.user.shortName) {
      const txt = `Login er allerede linket til en anden bruger. Overdrag dette login til brugeren ${this.vm.user.shortName}?`;
      this.ui.confirm(txt).then(
        action => {
          if (action = 'ok') {
            this.validateIdentityEmailConfirmed(identity);
          }
        }
      );
    }
    else {
      this.validateIdentityEmailConfirmed(identity);
    }
  }

  private validateIdentityEmailConfirmed(identity: IdentityDto) {
    if (identity.emailConfirmed == false) {

      const txt = `<p>Login for '${identity.email}' er ikke aktiveret til logind.</p>` +
        `<p>Ønskes logind aktiveret og tilknyttet brugeren?</p>` +
        `<p class='note'>Bemærk: Det kan udgøre en sikkerhedsrisiko, når brugeren ikke har bekræftet logind.</p>`;
      this.ui.confirm(txt).then(
        action => {
          if (action = 'ok') {
            this.close(true, identity.id, true);
          }
        }
      )
    }
    else {
      this.close(true, identity.id, false);
    }
  }

  public close(dataChanged: boolean, identityId: string = null, confirmEmail: boolean = null) {
    this.dialogRef.close({ dataChanged: dataChanged, identityId: identityId, confirmEmail: confirmEmail });
  }

}
